import { type ClipboardEventHandler, useRef } from 'react';

import { Button, Field, HStack, Tag } from '@rtkwlf/fenrir-react';
import Papa from 'papaparse';
import type { Control, FieldPathByValue, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Autocomplete, TextField } from '@packages/fenrir/components';
import { useGetCountryName } from '@packages/hooks/use-get-country-name/use-get-country-name';
import {
  COUNTRY_CODE_LIST,
  matchFirstCountryCode,
} from '@packages/utils/country.utils';

export interface ControlledCountryAutocompleteProps<
  T extends FieldValues,
  Name extends FieldPathByValue<T, undefined | string | string[]>,
> {
  control: Control<T>;
  name: Name;
  multiple: boolean;
  required?: boolean;
  label?: string;
  placeholder?: string;
}

export function ControlledCountryAutocomplete<
  T extends FieldValues,
  Name extends FieldPathByValue<T, undefined | string | string[]>,
>({
  control,
  name,
  multiple,
  required,
  label,
  placeholder,
}: ControlledCountryAutocompleteProps<T, Name>) {
  const { t } = useTranslation();
  const inputRef = useRef<null | HTMLInputElement>(null);
  const getCountryName = useGetCountryName();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        // `field.value` is ensured to be `undefined | string | string[]` in the call-site. However, TSC cannot infer that here.
        // So, added `undefined | string | string[]` here to ensure type safety for the operations in rest of this file.
        const fieldValue =
          (field.value as undefined | string | string[]) ??
          (multiple ? [] : '');

        const handlePaste: ClipboardEventHandler<HTMLDivElement> = (event) => {
          event.preventDefault();

          const pastedValue = event.clipboardData.getData('text');
          const csvLines = Papa.parse<string[]>(pastedValue, {
            transform: matchFirstCountryCode,
          }).data;

          const newCountryCodes = csvLines
            .flatMap((row) => row)
            .filter((value) => !!value);

          if (newCountryCodes.length) {
            const uniqueCountryCodes = new Set([
              ...fieldValue,
              ...newCountryCodes,
            ]);
            field.onChange([...uniqueCountryCodes]);
          }
        };

        const handleRemove = (countryCode: string) => {
          if (Array.isArray(fieldValue)) {
            field.onChange(fieldValue.filter((code) => code !== countryCode));
          }
        };

        const handleRemoveAll = () => {
          field.onChange(multiple ? [] : '');
          inputRef.current?.focus();
        };

        return (
          <Field.Root
            appearance={fieldState.error?.message ? 'danger' : undefined}
            isRequired={required}
          >
            {label && <Field.Label htmlFor={field.name}>{label}</Field.Label>}

            <Field.Content>
              <HStack>
                <Autocomplete
                  {...field}
                  value={fieldValue}
                  multiple={multiple}
                  clearOnBlur
                  openOnFocus
                  disableClearable
                  size="small"
                  id={field.name}
                  sx={{
                    maxWidth: 200,
                  }}
                  options={COUNTRY_CODE_LIST}
                  getOptionLabel={getCountryName}
                  onChange={(event, value) => field.onChange(value)}
                  renderTags={() => null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      id={field.name}
                      placeholder={placeholder || t('ui:chooseOption')}
                      onPaste={handlePaste}
                      inputRef={inputRef}
                      InputProps={{
                        ...params.InputProps,
                        id: field.name,
                        sx: {
                          minWidth: 200,
                        },
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Backspace') {
                          // prevent removing tags with Backspace
                          event.stopPropagation();
                        } else if (event.key === 'Enter') {
                          event.preventDefault();
                        }
                      }}
                    />
                  )}
                />

                {!!fieldValue.length && (
                  <Button
                    variant="link"
                    appearance="danger"
                    onClick={handleRemoveAll}
                  >
                    {multiple ? t('ui:removeAll') : t('ui:clear')}
                  </Button>
                )}
              </HStack>

              {multiple && Array.isArray(fieldValue) && !!fieldValue.length && (
                <HStack
                  marginTop="smedium"
                  flexWrap="wrap"
                  gap="small"
                  maxWidth="30rem"
                >
                  {fieldValue.map((countryCode) => (
                    <Tag
                      key={countryCode}
                      itemKey={countryCode}
                      variant="secondary"
                      isRemoveable
                      onRemoveClick={() => handleRemove(countryCode)}
                    >
                      {getCountryName(countryCode)}
                    </Tag>
                  ))}
                </HStack>
              )}
            </Field.Content>

            <Field.Message>{fieldState.error?.message}</Field.Message>
          </Field.Root>
        );
      }}
    />
  );
}
